<template>
  <div class="shop">
    <div class="shop-header">
      <h1>Shop</h1>
      <!-- Cart button with item count -->
      <button @click="showCart = true" class="cart-button">
        <i class="fas fa-shopping-cart"></i>
        <span v-if="cartItemCount > 0" class="cart-count">{{ cartItemCount }}</span>
      </button>
    </div>
    
    <!-- Admin auth required message - only shown to admins -->
    <div v-if="authRequired && isAdmin" class="auth-message">
      <p>Admin authentication required to access the shop.</p>
      <button @click="authenticate" class="btn btn-primary">Connect to Zettle</button>
    </div>
    
    <!-- Error message -->
    <div v-else-if="error" class="error-message">
      {{ error }}
    </div>
    
    <!-- Loading spinner -->
    <div v-else-if="loading" class="loading-container">
      <div class="spinner"></div>
      <p>Loading products...</p>
    </div>
    
    <!-- Products grid -->
    <div class="products-grid" v-else-if="products.length">
      <div v-for="product in products" :key="product.uuid" class="product-card">
        <!-- Use v-if to check if imageUrl exists, otherwise show a placeholder -->
        <div class="product-image">
          <img 
            v-if="product.presentation && product.presentation.imageUrl" 
            :src="product.presentation.imageUrl" 
            :alt="product.name"
          >
          <div v-else class="placeholder-image">No Image Available</div>
        </div>
        
        <div class="product-details">
          <h3>{{ product.name || 'Unnamed Product' }}</h3>
          <p class="description">{{ product.description || 'No description available' }}</p>
          <p class="price">£{{ ((product.variants && product.variants[0] && product.variants[0].price && product.variants[0].price.amount) ? (product.variants[0].price.amount / 100) : 0).toFixed(2) }}</p>
          <button 
            @click="addToCart(product)" 
            class="btn btn-primary"
            :disabled="!isInStock(product)"
          >
            {{ isInStock(product) ? 'Add to Cart' : 'Out of Stock' }}
          </button>
        </div>
      </div>
    </div>
    
    <!-- No products state -->
    <div v-else class="no-products">
      <p>No products available.</p>
    </div>
    
    <!-- Shopping Cart Modal -->
    <div v-if="showCart" class="cart-modal">
      <div class="modal-overlay" @click="showCart = false"></div>
      <div class="modal-content">
        <button @click="showCart = false" class="close-button">
          <i class="fas fa-times"></i>
        </button>
        <ShoppingCart @close="showCart = false" />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { getAuth } from 'firebase/auth'
import { useStore } from 'vuex'
import ShoppingCart from '@/components/ShoppingCart.vue'

export default {
  name: 'Shop',
  components: {
    ShoppingCart
  },
  
  setup() {
    const store = useStore()
    const products = ref([])
    const loading = ref(true)
    const error = ref(null)
    const authRequired = ref(false)
    const isAdmin = ref(false)
    const showCart = ref(false)
    
    // Get cart item count from store
    const cartItemCount = computed(() => store.getters['cart/cartItemCount'])

    // Check if the current user is an admin
    const checkAdminStatus = async () => {
      const auth = getAuth()
      const user = auth.currentUser
      
      if (user) {
        // Get the ID token with claims
        const idTokenResult = await user.getIdTokenResult()
        isAdmin.value = idTokenResult.claims.admin === true
      } else {
        isAdmin.value = false
      }
    }

    // Helper function to check if a product is in stock
    const isInStock = (product) => {
      // Check if product has variants
      if (!product.variants || !product.variants.length) {
        return false
      }
      
      const variant = product.variants[0]
      
      // According to Zettle API docs, check inventory
      if (variant.inventory) {
        // If tracked is false, the product is always in stock
        if (variant.inventory.tracked === false) {
          return true
        }
        
        // If tracked is true, check the quantity
        return variant.inventory.tracked === true && 
               variant.inventory.inStock !== undefined && 
               variant.inventory.inStock > 0
      }
      
      // For backward compatibility, also check the old inStock property
      if (variant.inStock !== undefined) {
        return variant.inStock
      }
      
      // Default to in stock if we can't determine (better user experience)
      return true
    }

    const authenticate = async () => {
      // Only admins should be able to authenticate
      if (!isAdmin.value) {
        error.value = 'You do not have permission to perform this action'
        return
      }
      
      try {
        const functions = getFunctions()
        const getAuthUrl = httpsCallable(functions, 'getAuthUrl')
        const result = await getAuthUrl()
        
        if (result.data.authUrl) {
          // Redirect to the Zettle authorization page
          window.location.href = result.data.authUrl
        } else {
          error.value = 'Failed to generate authentication URL'
        }
      } catch (err) {
        console.error('Authentication error:', err)
        error.value = 'Failed to start authentication process'
      }
    }

    const fetchProducts = async () => {
      try {
        loading.value = true
        error.value = null
        
        const functions = getFunctions()
        const getProducts = httpsCallable(functions, 'getProductsV2')
        const result = await getProducts()
        
        // Check if there's an error in the response
        if (result.data.error) {
          console.error('API Error:', result.data.error)
          
          // Check if authentication is required (only for admins)
          if (result.data.error.status === 'AUTH_REQUIRED' && result.data.error.details?.isAdmin) {
            await checkAdminStatus()
            authRequired.value = isAdmin.value // Only show auth required if user is admin
            if (!isAdmin.value) {
              error.value = 'Products are temporarily unavailable. Please check back later.'
            }
            return
          } else if (result.data.error.status === 'UNAVAILABLE') {
            // Regular users see this message
            error.value = result.data.error.message
            return
          }
          
          // Handle other errors
          error.value = result.data.error.message || 'Failed to load products. Please try again later.'
          return
        }
        
        // Get products from the response
        const fetchedProducts = result.data.products || []
        
        // Log the first product to see its structure
        if (fetchedProducts.length > 0) {
          console.log('Product structure example:', fetchedProducts[0])
          
          // Log inventory information specifically
          if (fetchedProducts[0].variants && fetchedProducts[0].variants.length > 0) {
            console.log('Variant inventory:', fetchedProducts[0].variants[0].inventory)
          }
        }
        
        // Sort products by created timestamp in descending order (newest first)
        // According to Zettle API docs, the field is likely 'created'
        products.value = fetchedProducts.sort((a, b) => {
          // Try different possible date fields
          const dateA = a.created || a.createTime || a.createdAt || a.timestamp || 0
          const dateB = b.created || b.createTime || b.createdAt || b.timestamp || 0
          
          // If dates are strings, convert to timestamps
          const timeA = typeof dateA === 'string' ? new Date(dateA).getTime() : dateA
          const timeB = typeof dateB === 'string' ? new Date(dateB).getTime() : dateB
          
          // Sort descending (newest first)
          return timeB - timeA
        })
        
        console.log(`Successfully sorted ${products.value.length} products by date`)
      } catch (err) {
        console.error('Error fetching products:', err)
        error.value = 'Failed to load products. Please try again later.'
      } finally {
        loading.value = false
      }
    }

    const addToCart = async (product) => {
      if (!isInStock(product)) {
        console.log('Product not in stock:', product)
        return
      }
      
      // Log the product being added to cart for debugging
      console.log('Adding to cart:', product)
      
      try {
        const result = await store.dispatch('cart/addProductToCart', { product, quantity: 1 })
        
        if (result && result.success) {
          // Show success notification
          store.commit('SET_NOTIFICATION', {
            type: 'success',
            message: `${product.name} added to cart`
          })
        } else if (result && !result.success) {
          // Show error notification with specific error message
          store.commit('SET_NOTIFICATION', {
            type: 'error',
            message: result.error || 'Failed to add product to cart'
          })
        }
      } catch (error) {
        console.error('Error adding to cart:', error)
        store.commit('SET_NOTIFICATION', {
          type: 'error',
          message: 'Failed to add product to cart'
        })
      }
    }

    onMounted(async () => {
      // Check admin status first
      await checkAdminStatus()
      
      // Check if we're returning from OAuth flow
      const urlParams = new URLSearchParams(window.location.search)
      if (urlParams.get('auth') === 'success') {
        // Clear the URL parameter
        window.history.replaceState({}, document.title, '/shop')
      }
      
      fetchProducts()
    })

    return {
      products,
      loading,
      error,
      authRequired,
      isAdmin,
      authenticate,
      addToCart,
      isInStock,
      showCart,
      cartItemCount
    }
  }
}
</script>

<style lang="scss" scoped>
.shop {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  
  .shop-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;
    
    h1 {
      font-size: 2.5rem;
      color: #333;
      margin: 0;
    }
    
    .cart-button {
      position: relative;
      background: #007bff;
      color: white;
      border: none;
      border-radius: 50%;
      width: 50px;
      height: 50px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: background-color 0.2s;
      
      &:hover {
        background-color: #0069d9;
      }
      
      i {
        font-size: 1.5rem;
      }
      
      .cart-count {
        position: absolute;
        top: -5px;
        right: -5px;
        background-color: #dc3545;
        color: white;
        border-radius: 50%;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.8rem;
        font-weight: bold;
      }
    }
  }

  .auth-message {
    text-align: center;
    margin: 2rem auto;
    max-width: 600px;
    padding: 1.5rem;
    background-color: #f8f9fa;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    
    button {
      margin-top: 1rem;
      padding: 0.5rem 1.5rem;
      font-weight: 500;
    }
  }

  .error-message {
    color: #721c24;
    text-align: center;
    margin: 2rem auto;
    max-width: 600px;
    padding: 1.5rem;
    background-color: #f8d7da;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  }

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin: 4rem 0;
    
    p {
      margin-top: 1rem;
      color: #666;
      font-size: 1.1rem;
    }
    
    .spinner {
      width: 50px;
      height: 50px;
      border: 5px solid rgba(0, 0, 0, 0.1);
      border-radius: 50%;
      border-top-color: #007bff;
      animation: spin 1s ease-in-out infinite;
    }
    
    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }
  }
  
  .no-products {
    text-align: center;
    margin: 4rem 0;
    color: #666;
    font-size: 1.1rem;
  }

  .products-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 2rem;
    margin-top: 2rem;
  }

  .product-card {
    display: flex;
    flex-direction: column;
    border: 1px solid #eaeaea;
    border-radius: 12px;
    overflow: hidden;
    transition: transform 0.2s, box-shadow 0.2s;
    background-color: white;
    height: 100%;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
    
    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    }

    .product-image {
      height: 220px;
      overflow: hidden;
      
      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform 0.3s;
        
        &:hover {
          transform: scale(1.05);
        }
      }
      
      .placeholder-image {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #f8f9fa;
        color: #6c757d;
        font-style: italic;
      }
    }
    
    .product-details {
      padding: 1.5rem;
      display: flex;
      flex-direction: column;
      flex-grow: 1;
      
      h3 {
        margin: 0 0 1rem 0;
        font-size: 1.2rem;
        color: #333;
      }

      .description {
        color: #666;
        margin-bottom: 1rem;
        flex-grow: 1;
        font-size: 0.95rem;
        line-height: 1.5;
      }

      .price {
        font-size: 1.3rem;
        font-weight: bold;
        margin-bottom: 1.2rem;
        color: #2c3e50;
      }

      button {
        padding: 0.6rem 1rem;
        font-weight: 500;
        border-radius: 6px;
        transition: background-color 0.2s;
        
        &:disabled {
          background-color: #ccc;
          cursor: not-allowed;
        }
        
        &:not(:disabled):hover {
          background-color: #0069d9;
        }
      }
    }
  }
  
  .cart-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    
    .modal-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.5);
    }
    
    .modal-content {
      position: relative;
      width: 90%;
      max-width: 900px;
      max-height: 90vh;
      overflow-y: auto;
      z-index: 1001;
      
      .close-button {
        position: absolute;
        top: 1rem;
        right: 1rem;
        background: none;
        border: none;
        color: #333;
        font-size: 1.5rem;
        cursor: pointer;
        z-index: 1002;
        
        &:hover {
          color: #dc3545;
        }
      }
    }
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
    
    .shop-header {
      h1 {
        font-size: 2rem;
      }
      
      .cart-button {
        width: 45px;
        height: 45px;
        
        i {
          font-size: 1.2rem;
        }
      }
    }
    
    .products-grid {
      grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
      gap: 1.5rem;
    }
    
    .product-card {
      .product-image {
        height: 180px;
      }
      
      .product-details {
        padding: 1rem;
        
        h3 {
          font-size: 1.1rem;
        }
        
        .price {
          font-size: 1.2rem;
        }
      }
    }
    
    .cart-modal .modal-content {
      width: 95%;
      max-height: 95vh;
    }
  }
}
</style> 